/**
 * 畫布存檔（本機那一份）。
 *
 * 畫布有兩份：這台瀏覽器的 localStorage，跟後端 employee.warRoomLayout
 * （見 ./remote.ts）。兩份存的是同一個 payload，靠 savedAt 比新舊。
 * localStorage 是快取兼離線備援：打開頁面先用它立刻畫出來，再跟後端對一次。
 *
 * 只存「使用者自己排出來的東西」：圖卡、它的 spec、位置大小、深淺色。
 * 抓回來的 rows 一律不存 —— 那是 API 資料，寫進 localStorage 既會撐爆容量
 * （5MB），也等於把公司資料留在這台機器上，而且下次打開一定是過期的。
 * 還原之後重新抓一次才是對的。
 *
 * 存檔按公司分開。切公司走的是 SPA 換頁、不會重新整理，
 * 所以 key 每次讀寫都重算，不能在 module load 時就固定下來。
 */
import { clampPos, clampSize } from "@/warRoom/canvas/layout";
import { datasetByKey } from "@/warRoom/datasets/registry";
import { emptySpec, type GraphSpec } from "@/warRoom/graph/spec";
import type { Mode } from "@/warRoom/theme/palette";

/** 存檔格式改過就換版號 —— 舊存檔直接丟掉，不做遷移 */
const VERSION = 2;
const KEY_PREFIX = "warRoomCanvas";

export type SavedWidget = {
  id: string;
  spec: GraphSpec;
  viewMode: "chart" | "table";
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SavedCanvas = {
  widgets: SavedWidget[];
  activeDatasetKey: string | null;
  mode: Mode;
  savedAt: string;
};

export type CanvasPayload = SavedCanvas & { version: number; companyId: string };

/** 沒有 companyId 就用 "none" —— 還是存得起來，只是不跟任何公司綁 */
export function currentCompanyId(): string {
  try {
    return localStorage.getItem("companyId") || "none";
  } catch {
    return "none";
  }
}

const storageKey = (companyId = currentCompanyId()) =>
  `${KEY_PREFIX}:v${VERSION}:${companyId}`;

/* --------------------------------- 讀 --------------------------------- */

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown, fallback: string | null = null) =>
  typeof v === "string" ? v : fallback;

const num = (v: unknown, fallback: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

/**
 * 把存檔裡的 spec 拉回合法形狀。
 *
 * 這裡只做「型別對不對」，不管「這份資料畫不畫得出這種圖」——
 * 後者交給 store 的 reconcile，資料集欄位改過的舊存檔會在那裡被修好。
 */
function sanitizeSpec(raw: unknown): GraphSpec | null {
  if (!isObj(raw)) return null;

  // 資料集不存在（registry 改過、或存檔被改壞）就整張圖丟掉 —— 沒資料的圖卡沒有意義
  const datasetKey = str(raw.datasetKey);
  if (!datasetKey || !datasetByKey(datasetKey)) return null;

  const dimFilters: Record<string, string[]> = {};
  if (isObj(raw.dimFilters)) {
    for (const [k, v] of Object.entries(raw.dimFilters)) {
      if (Array.isArray(v)) {
        const values = v.filter((x): x is string => typeof x === "string");
        if (values.length > 0) dimFilters[k] = values;
      }
    }
  }

  const range = raw.dateRange;
  const dateRange: [string, string] | null =
    Array.isArray(range) &&
    range.length === 2 &&
    typeof range[0] === "string" &&
    typeof range[1] === "string"
      ? [range[0], range[1]]
      : null;

  return {
    ...emptySpec,
    datasetKey,
    chartType: (str(raw.chartType) as GraphSpec["chartType"]) ?? null,
    xField: str(raw.xField),
    yField: str(raw.yField),
    aggregation:
      (str(raw.aggregation) as GraphSpec["aggregation"]) ??
      emptySpec.aggregation,
    seriesField: str(raw.seriesField),
    timeGrain:
      (str(raw.timeGrain) as GraphSpec["timeGrain"]) ?? emptySpec.timeGrain,
    topN: num(raw.topN, emptySpec.topN),
    sortBy: (str(raw.sortBy) as GraphSpec["sortBy"]) ?? emptySpec.sortBy,
    dateField: str(raw.dateField),
    dateRange,
    dimFilters,
  };
}

function sanitizeWidget(raw: unknown, seen: Set<string>): SavedWidget | null {
  if (!isObj(raw)) return null;
  const id = str(raw.id);
  if (!id || seen.has(id)) return null;
  const spec = sanitizeSpec(raw.spec);
  if (!spec) return null;

  seen.add(id);
  return {
    id,
    spec,
    viewMode: raw.viewMode === "table" ? "table" : "chart",
    ...clampPos(num(raw.x, 0), num(raw.y, 0)),
    ...clampSize(num(raw.w, 0), num(raw.h, 0)),
  };
}

export function parseCanvas(parsed: unknown): SavedCanvas | null {
  if (!isObj(parsed) || !Array.isArray(parsed.widgets)) return null;
  if (parsed.version !== undefined && parsed.version !== VERSION) return null;

  const seen = new Set<string>();
  const widgets = parsed.widgets
    .map((w) => sanitizeWidget(w, seen))
    .filter((w): w is SavedWidget => w !== null);
  if (widgets.length === 0) return null;

  const activeDatasetKey = str(parsed.activeDatasetKey);
  return {
    widgets,
    activeDatasetKey:
      activeDatasetKey && datasetByKey(activeDatasetKey)
        ? activeDatasetKey
        : null,
    mode: parsed.mode === "dark" ? "dark" : "light",
    savedAt: str(parsed.savedAt) ?? "",
  };
}

/**
 * 讀這間公司的存檔。壞掉、過期、空的一律當成沒有存檔（回 null），
 * 不要讓一筆壞資料把整個戰情室卡住。
 */
export function readCanvas(companyId = currentCompanyId()): SavedCanvas | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey(companyId));
  } catch {
    return null; // 無痕模式／停用 cookie
  }
  if (!raw) return null;

  try {
    return parseCanvas(JSON.parse(raw));
  } catch {
    return null;
  }
}

/* --------------------------------- 寫 --------------------------------- */

export function toPayload(
  canvas: Omit<SavedCanvas, "savedAt"> & { savedAt?: string },
  companyId = currentCompanyId(),
): CanvasPayload {
  return {
    version: VERSION,
    companyId,
    savedAt: canvas.savedAt ?? new Date().toISOString(),
    widgets: canvas.widgets.map((w) => ({
      id: w.id,
      spec: w.spec,
      viewMode: w.viewMode,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
    })),
    activeDatasetKey: canvas.activeDatasetKey,
    mode: canvas.mode,
  };
}

export function canvasFingerprint(
  canvas: Omit<SavedCanvas, "savedAt">,
): string {
  const { widgets, activeDatasetKey, mode } = toPayload(canvas, "");
  return JSON.stringify({ widgets, activeDatasetKey, mode });
}

/**
 * 存這間公司的畫布。空畫布直接把 key 刪掉 ——
 * 「清空畫布」之後重新整理不該又把圖變回來。
 */
export function writeCanvas(
  canvas: Omit<SavedCanvas, "savedAt"> & { savedAt?: string },
  companyId = currentCompanyId(),
): CanvasPayload {
  const payload = toPayload(canvas, companyId);
  try {
    const key = storageKey(companyId);
    if (payload.widgets.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(payload));
    }
  } catch {
    // 容量爆了或存不進去就算了 —— 存檔失敗不該打斷正在畫圖的人
  }
  return payload;
}

export function clearCanvasStorage(companyId = currentCompanyId()): void {
  try {
    localStorage.removeItem(storageKey(companyId));
  } catch {
    /* 同上 */
  }
}
